"use client";

import type { ReactNode } from "react";

import { SelectAllIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import type { LinkTarget } from "@/components/link";
import { ToggleGroupOptionItem, ToggleGroupRoot } from "@/components/ui/toggle-group";
import { useFormatNumber } from "@/hooks/use-format-number";

interface DiscographyTypeOption {
  key: string;
  label: string;
  icon: AppIcon;
}

interface DiscographyTypeHeaderProps {
  title: ReactNode;
  /** Muted text next to the title, e.g. the total album count. */
  subtitle?: ReactNode;
  allLabel: string;
  options: DiscographyTypeOption[];
  counts: Record<string, number>;
  selectedType: string;
  /** Hide the tabs until the default type has been resolved. */
  showTabs: boolean;
  onTypeChange: (type: string) => void;
  getTypeLinkTarget?: (type: string) => LinkTarget;
}

/**
 * Discography title and underlined release-type tabs. The tabs never wrap: they
 * scroll with a fade and a "›" hint when they overflow. Tabs are links, so every
 * type view stays crawlable.
 */
export function DiscographyTypeHeader({
  title,
  subtitle,
  allLabel,
  options,
  counts,
  selectedType,
  showTabs,
  onTypeChange,
  getTypeLinkTarget,
}: DiscographyTypeHeaderProps) {
  const formatNumber = useFormatNumber();
  const visibleOptions = options.filter(
    (option) => (counts[option.key] ?? 0) > 0 || selectedType === option.key,
  );

  return (
    <div className="flex flex-col gap-1 border-b border-border lg:flex-row lg:items-end lg:gap-8">
      <div className="flex shrink-0 items-baseline gap-3 lg:pb-2">
        <h2 className="text-xl font-bold tracking-tight md:text-2xl">{title}</h2>
        {subtitle && <span className="text-sm text-muted-foreground">{subtitle}</span>}
      </div>

      {showTabs && (
        <ToggleGroupRoot
          wrapperClassName="-mb-px min-w-0 flex-1"
          scrollHint
          value={[selectedType]}
          onValueChange={(values) => {
            if (values.length > 0) onTypeChange(values[0]);
          }}
          className="flex w-max gap-5 pr-10"
        >
          <ToggleGroupOptionItem
            variant="tab"
            value="all"
            icon={SelectAllIcon}
            badge={formatNumber(counts.all ?? 0)}
            link={getTypeLinkTarget?.("all")}
          >
            {allLabel}
          </ToggleGroupOptionItem>
          {visibleOptions.map((option) => (
            <ToggleGroupOptionItem
              variant="tab"
              key={option.key}
              value={option.key}
              icon={option.icon}
              badge={formatNumber(counts[option.key] ?? 0)}
              link={getTypeLinkTarget?.(option.key)}
            >
              {option.label}
            </ToggleGroupOptionItem>
          ))}
        </ToggleGroupRoot>
      )}
    </div>
  );
}
